import {SankeyLink, SankeyNode} from 'd3-sankey';
import {chain} from 'lodash';
import React, {useEffect, useRef, useState} from 'react';

import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {AnalyticsTableMetricsEnum} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {getAnalyticsDetailsSelectedMetricWithOptions} from '../../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {
  ContributingSegmentsOptionsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {AnalyticsComputedGraphMetricsEnum} from '../../../transformers/analyticsMetricsTransformersTypes';
import {
  getSankeySubmetricLabel,
  SankeyGraphMetric,
  SankeySubmetricLinksEnum,
  SankeySubmetricNodesEnum,
} from '../../../transformers/sankeyTableToGraph';
import {Sankey, SLinkExtra, SNodeExtra} from '../../charts/components/sankey/sankey';
import {useChartTooltip} from '../../charts/helpers/useChartTooltip';
import {StyledElementWrapper} from '../../reportViewer/reportCommons';
import {ReportElementProps, ReportElementWrapper, ReportElementWrapperProps} from '../reportElement';
import {ChartProps} from './chartsModel';
import {MetricErrorState} from './common/metricErrorState';
import {defaultEmptyStateRenderer, isEmptyMetric} from './emptyState';
import {SankeyTooltip} from './sankeyTooltip';
import {useGraphMetricData} from './useGraphMetricData';

/*
 * Constants.
 */

const DEFAULT_HEIGHT = 302;

const mockData = [
  {
    id: 0,
    links: [
      {
        targetId: 1,
        value: 30,
      },
      {
        targetId: 4,
        value: 20,
      },
    ],
  },
  {
    id: 1,
    links: [
      {
        targetId: 2,
        value: 10,
      },
      {
        targetId: 3,
        value: 30,
      },
    ],
  },
  {
    id: 2,
    links: [],
  },
  {
    id: 3,
    links: [],
  },
  {
    id: 4,
    links: [
      {
        targetId: 2,
        value: 5,
      },
      {
        targetId: 3,
        value: 55,
      },
    ],
  },
  {
    id: 5,
    links: [
      {
        targetId: 1,
        value: 10,
      },
      {
        targetId: 4,
        value: 40,
      },
    ],
  },
];

/*
 * Props.
 */

export interface ReportSankeyChartProps
  extends ReportElementWrapperProps,
    ChartProps,
    ReportElementProps<AnalyticsComputedGraphMetricsEnum.SANKEY_GRAPH> {}

/*
 * Component.
 */

export const ReportSankeyChart = ({
  title,
  infoTooltip,
  reportSectionId,
  renderEmptyState = defaultEmptyStateRenderer,
  isLoading,
  metric,
  width,
  height,
}: ReportSankeyChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSizeInfo = useSize(containerRef);
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const {selectedMetric} = useAppSelector(getAnalyticsDetailsSelectedMetricWithOptions);
  const [selectedNodeId, setSelectedNodeId] = useState<string>();
  const [selectedLinkId, setSelectedLinkId] = useState<string>();

  useEffect(() => {
    if (selectedMetric !== AnalyticsTableMetricsEnum.SANKEY_TABLE) {
      // Reset selected node & link if selected metric is not sankey.
      setSelectedNodeId(undefined);
      setSelectedLinkId(undefined);
    }
  }, [selectedMetric]);

  const [graphMetricData, metricError] = useGraphMetricData(
    metric,
    (metricData?: SankeyGraphMetric) => isLoading && (!metricData || metricData.values?.length === 0),
    mockData,
  );

  const [setTooltipInfo, maybeRenderTooltip] =
    useChartTooltip<SankeyLink<SNodeExtra, SLinkExtra>>(tooltipRenderer);

  // We cannot pass an empty array to the Sankey component.
  const isEmpty =
    isEmptyMetric(graphMetricData) ||
    chain(graphMetricData?.values || [])
      // Sum all link values
      // @ts-expect-error
      .map((stage) => stage.links)
      .flatten()
      .reduce((acc, link) => acc + link.value, 0)
      .value() === 0;

  const onNodeSelect = (node: SankeyNode<SNodeExtra, SLinkExtra>) => {
    const nodeId = node.id as SankeySubmetricNodesEnum;
    setSelectedNodeId(nodeId);
    setSelectedLinkId(undefined);
    dispatch(
      setMetricDetailsAction(
        AnalyticsTableMetricsEnum.SANKEY_TABLE,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: nodeId,
          },
        },
        getSankeySubmetricLabel(localize, nodeId),
      ),
    );
  };

  const onLinkSelect = (link: SankeyLink<SNodeExtra, SLinkExtra>) => {
    const linkId = link.id as SankeySubmetricLinksEnum;
    setSelectedLinkId(linkId);
    setSelectedNodeId(undefined);
    dispatch(
      setMetricDetailsAction(
        AnalyticsTableMetricsEnum.SANKEY_TABLE,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: linkId,
          },
        },
        getSankeySubmetricLabel(localize, linkId),
      ),
    );
  };

  const renderChart = () => {
    if (metricError) {
      return (
        <MetricErrorState
          error={metricError}
          width={width ?? containerSizeInfo?.width}
          height={height ?? DEFAULT_HEIGHT}
        />
      );
    }

    if (isEmpty) {
      return renderEmptyState?.({width: width ?? containerSizeInfo?.width, height: height ?? DEFAULT_HEIGHT});
    }

    if (containerSizeInfo) {
      return (
        <Sankey
          width={width ?? containerSizeInfo.width}
          height={height ?? DEFAULT_HEIGHT}
          isLoading={isLoading}
          data={[...(graphMetricData?.values ?? [])]}
          onHover={setTooltipInfo}
          onUnhover={() => setTooltipInfo(undefined, undefined)}
          onNodeClick={onNodeSelect}
          onLinkClick={onLinkSelect}
          selectedNodeId={selectedNodeId}
          selectedLinkId={selectedLinkId}
        />
      );
    }

    return null;
  };

  return (
    <ReportElementWrapper title={title} reportSectionId={reportSectionId} infoTooltip={infoTooltip}>
      <StyledElementWrapper ref={containerRef}>
        {renderChart()}
        {maybeRenderTooltip()}
      </StyledElementWrapper>
    </ReportElementWrapper>
  );
};

/*
 * Helpers.
 */

function tooltipRenderer(bounds: DOMRect, tooltipData: SankeyLink<SNodeExtra, SLinkExtra>) {
  return <SankeyTooltip bounds={bounds} data={tooltipData} />;
}
